import { useState } from "react";
import { Link } from "wouter";
import { useFleetUptime, type UptimeRow } from "../lib/api.js";

const windows = [
  { days: 1, label: "24h" },
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

function tier(pct: number): "ok" | "warn" | "bad" | "muted" {
  if (pct >= 99) return "ok";
  if (pct >= 95) return "warn";
  return "bad";
}

const toneClass: Record<"ok" | "warn" | "bad" | "muted", string> = {
  ok: "text-emerald-300",
  warn: "text-amber-300",
  bad: "text-rose-300",
  muted: "text-zinc-500",
};

function bar(pct: number) {
  const t = tier(pct);
  const fill: Record<typeof t, string> = {
    ok: "bg-emerald-500/60",
    warn: "bg-amber-500/60",
    bad: "bg-rose-500/60",
    muted: "bg-zinc-600",
  };
  return (
    <div className="h-1.5 w-full rounded-full bg-zinc-800">
      <div
        className={`h-full rounded-full ${fill[t]}`}
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
  );
}

function fmtDays(rows: UptimeRow[], days: number): string {
  if (rows.length === 0) return "no snapshots yet";
  const earliest = Math.min(...rows.map((r) => r.since));
  const ageMs = Date.now() - earliest;
  const ageH = Math.round(ageMs / 3600_000);
  if (ageH < days * 24) return `coverage ~${ageH}h (less than the ${days}d window)`;
  return `${days}d coverage`;
}

export function UptimeSLA() {
  const [days, setDays] = useState(7);
  const { data, isLoading, error } = useFleetUptime(days);
  const rows = data ?? [];

  const totalSamples = rows.reduce((a, b) => a + b.samples, 0);
  const meanOnline =
    rows.length > 0
      ? rows.reduce((a, b) => a + b.onlinePct, 0) / rows.length
      : 0;

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <header className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">
            Uptime SLA
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            % of snapshots reporting online over the selected window. Cabinets ranked
            worst-first. Snapshots arrive every 5 min via Cron Worker.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-zinc-800 bg-zinc-950 p-1">
          {windows.map((w) => (
            <button
              key={w.days}
              onClick={() => setDays(w.days)}
              className={
                "rounded-md px-3 py-1 text-xs font-medium transition " +
                (days === w.days
                  ? "bg-zinc-800 text-zinc-100"
                  : "text-zinc-400 hover:text-zinc-200")
              }
            >
              {w.label}
            </button>
          ))}
        </div>
      </header>

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3">
        <div className="card p-5">
          <div className="stat-label">Cabinets reporting</div>
          <div className="mt-2 stat-num">{rows.length}</div>
        </div>
        <div className="card p-5">
          <div className="stat-label">Mean online %</div>
          <div className={`mt-2 stat-num ${toneClass[tier(meanOnline)]}`}>
            {rows.length > 0 ? meanOnline.toFixed(1) : "—"}%
          </div>
        </div>
        <div className="card p-5">
          <div className="stat-label">Snapshots in window</div>
          <div className="mt-2 stat-num">{totalSamples.toLocaleString()}</div>
          <div className="mt-1 text-xs text-zinc-500">{fmtDays(rows, days)}</div>
        </div>
      </div>

      {error && (
        <div className="card p-6 text-sm text-rose-300">
          Failed to load: {(error as Error).message}
        </div>
      )}

      {isLoading && <div className="card p-10 text-center text-sm text-zinc-500">Loading…</div>}

      {!isLoading && rows.length === 0 && (
        <div className="card p-10 text-center text-sm text-zinc-400">
          No snapshots in the last {days}d yet — cron snapshots every 5 min, so this
          fills in over time. The first {days}d window is partial until you've been
          collecting that long.
        </div>
      )}

      {rows.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800/60 text-left text-[11px] uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3 font-medium">Cabinet</th>
                <th className="px-4 py-3 font-medium">Online %</th>
                <th className="px-4 py-3 font-medium">Samples</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.cabinetId} className="border-b border-zinc-800/60 last:border-b-0">
                  <td className="px-4 py-3 font-mono text-xs text-zinc-200">
                    <Link href={`/cabinets/${r.cabinetId}`} className="hover:text-zinc-50">
                      {r.cabinetId}
                    </Link>
                  </td>
                  <td className={`px-4 py-3 font-mono text-xs ${toneClass[tier(r.onlinePct)]}`}>
                    {r.onlinePct.toFixed(1)}%
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-400">{r.samples}</td>
                  <td className="w-1/3 px-4 py-3">{bar(r.onlinePct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
